import { useCallback, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';

import { useTranslation } from '@lyearn/i18n';
import { PlusOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Button, Stack, Typography } from '@lyearn/ui';
import { FilterOption } from '@/components/Filter/types';
import AddWorkspace from '@/modules/corporate/modules/workspaceSettings/components/AddWorkspace';
import useSubworkspaces from '@/modules/corporate/modules/workspaceSettings/hooks/useSubworkspaces';

import RemoveablePill from '../SkillMeasured/components/RemoveablePill';
import { QuestionSubworkspacesProps } from './types';

const QuestionSubworkspaces: React.FC<QuestionSubworkspacesProps> = (props) => {
  const { value, onChange, allowPartialUpdates } = props;
  const { t } = useTranslation();

  const { subWorkspaces, fetching } = useSubworkspaces();

  const selectedWorkspaces = subWorkspaces?.filter(
    (subWorkspace) => value?.includes(subWorkspace.id),
  );

  const onRemoveWorkspace = useCallback(
    (id: string) => {
      onChange(value?.filter((workspace) => workspace !== id) ?? []);
    },
    [onChange, value],
  );

  const selectedWorkspaceFilterValues: FilterOption[] = useMemo(
    () =>
      subWorkspaces?.length
        ? subWorkspaces
            .filter((subWorkspace) => value?.includes(subWorkspace.id))
            .map((item) => ({
              label: item.name,
              value: item.id,
            }))
        : [],
    [subWorkspaces, value],
  );

  const onWorkspaceFilterChange = useCallback(
    (val: FilterOption[]) => {
      onChange(val.map((item) => item.value));
    },
    [onChange],
  );

  return (
    <Stack direction="col">
      {allowPartialUpdates ? null : (
        <AddWorkspace
          disabled={allowPartialUpdates}
          selectedValues={selectedWorkspaceFilterValues}
          onChange={onWorkspaceFilterChange}>
          <Button
            appearance="secondary"
            className="self-start"
            shape="pill"
            size="x-small"
            startIcon={
              <PlusOutline className="w-16 h-16 -me-4" fillColorToken="icon-placeholder" />
            }
            variant="outlined">
            <Typography color="text-text-placeholder" variant="body-short-02">
              {t('QuizCreator:QuestionsSettings.AddWorkspaces')}
            </Typography>
          </Button>
          )
        </AddWorkspace>
      )}
      {isEmpty(value) || fetching ? null : (
        <Stack
          className={cn('flex w-full flex-1 flex-wrap content-start', {
            'mt-12': !allowPartialUpdates,
          })}
          gapX={8}
          gapY={12}>
          {selectedWorkspaces?.map((workspace) => {
            const { id, name } = workspace;

            return (
              <RemoveablePill
                key={id}
                allowUpdates={!allowPartialUpdates}
                id={id}
                name={name}
                onRemove={onRemoveWorkspace}
              />
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

export default QuestionSubworkspaces;
